import type { FC } from 'react';
import Icon from '@/components/UI/Icon';
import { capitalise, formatNumber } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import type { ID } from '@/model/Common.model';
import type { Treatment } from '@/model/Treatment.model';

interface ModalMetadataItem {
  field_type: string;
  glossary_id: ID;
  title: string;
  value: string | Array<string> | number;
}

interface ModalTreatment extends Omit<Treatment, 'fields' | 'temp_id' | 'metadata'> {
  meta: Record<string, unknown>;
  metadata: Array<ModalMetadataItem>;
  stock: { value?: string; unit: string };
  temp_id: ID;
  treatment_name: string;
  treatment_type_id: ID;
  fields: Array<{
    label: string;
    default_display_value: string | number;
    default_value: string | number;
    name: string;
    unit: {
      display_unit?: string;
      name: string;
    };
  }>;
}

interface TreatmentMetaProps {
  treatment: ModalTreatment;
  closeModal: () => void;
}

interface SectionProps {
  title: string;
  value: string;
}

const Section: FC<SectionProps> = ({ title, value }) => (
  <div className="bb b--moon-gray pa3 f6 break-word">
    <h4 className="basier-med lh-solid normal">{title}</h4>
    <p className="mt1 basier-reg normal lh-copy dark-gray">{value}</p>
  </div>
);

const TreatmentMeta: FC<TreatmentMetaProps> = ({ treatment, closeModal }) => {
  const metadata = treatment?.metadata ?? treatment?.meta ?? [];

  const shouldHideValue = (value: string | Array<string> | number): boolean => {
    if (!_notNil(value)) {
      return true;
    }

    if (Array.isArray(value)) {
      return _isEmpty(value);
    }

    const stringValue = String(value).trim();
    return stringValue.length === 0;
  };

  const formatFieldValue = (default_display_value: string | number, default_value: string | number): string => {
    const valueToFormat = default_display_value ?? default_value;

    const formatted = formatNumber(valueToFormat, true, 3);
    return String(formatted ?? '-');
  };

  const renderValue = (value: string | Array<string> | number, field_type: string): string | null => {
    if (shouldHideValue(value)) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((item: string) => safelyDecodeURIComponent(item)).join(', ');
    }

    if (field_type === 'date') {
      return DateUtils.renderDate(typeof value === 'string' ? value : String(value), {
        defaultResponse: '-',
      });
    }

    const decodedValue = safelyDecodeURIComponent(String(value));
    const trimmedValue = decodedValue.trim();

    // Normalize whitespace: replace multiple spaces with single space
    return trimmedValue.replace(/\s+/g, ' ');
  };

  return (
    <div className="ui-card center" style={{ width: '400px' }}>
      <div className="pa3 relative">
        <div className={'f5 ui__group-label relative w-100 pr3 black'}>
          <div className="lh-title">{treatment.treatment_type.name}</div>
        </div>
        <a onClick={closeModal} className="absolute right-0 top-0 ma2 w2 h2 pointer flex hover-near-black">
          <Icon icon="close" width="12" height="12" className="self-center center" />
        </a>
      </div>
      {treatment?.fields?.map(({ label, default_display_value, default_value, name, unit }) => {
        const displayUnit = `${unit.display_unit ?? unit.name}`;
        const displayValue = formatFieldValue(default_display_value, default_value);
        return (
          _isNotEmpty(default_value) && (
            <Section
              key={`field-${name}-${label || name}`}
              title={label ?? capitalise(name)}
              value={`${displayValue} ${displayUnit}`}
            />
          )
        );
      })}
      {metadata.map(({ title, value, field_type, glossary_id }) => {
        const renderedValue = renderValue(value, field_type);
        return renderedValue ? <Section key={`${glossary_id}-${title}`} title={title} value={renderedValue} /> : null;
      })}
    </div>
  );
};

export default TreatmentMeta;
